import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { debounce } from 'lodash';
import type { DesignGroup } from '../../types/storeReviewOrderStep.types';
import {
  FLAG_FILE_FOLDER,
  STORE_CONFIRM_SIZES_NOTE,
} from '../../constants/storeCheckout.constants';
import { buildStoreOrderItemSizesUpdatePayload } from '../../helpers/storeCheckoutOrderItemSizesUpdate.utils';
import {
  applySizeOrderLocksToSizeGroup,
  canToggleSizeSelection,
} from '../../helpers/storeCheckoutEditSizeLocks.utils';
import {
  buildSizeConfigurationLikeOrders,
  buildOrderItemSizesFromDesignAndSelected,
  convertOrderItemSizesToAllowedSizes,
  convertSizesToOrderItemFormat,
  getSelectedSizeLabels,
  getSizesLabel,
  sizesAvailableToRecord,
  sizesSelectedToRecord,
} from './storeSizeConfig.utils';
import {
  applySoldOutRestrictionsToSizesSelected,
  buildStocksDataForProduct,
  buildUnavailableStocksDataForProduct,
  createNoStockInfoMessages,
  isSizeRestrictedByStock,
  mergeSoldOutSizeDisplayState,
} from './storeConfirmSizesStock.utils';
import type {
  ISizeFormGroup,
  ISizeSelectedState,
  ISizesFormValues,
  IStocksData,
  IStoreOrderProduct,
} from './storeConfirmSizes.types';
import type { ICartItemDetail } from '@/common/interfaces/cart.interface';
import type { ISizeOrderCounts } from '@/common/interfaces/store.interface';
import { useLazyGetStocksQuery } from '@/api/stockQuery';
import { useUpdateStoreMutation } from '@/api/StoreQuery';
import { useGetS3PreSignedUrlMutation } from '@/api/S3Query';
import { showToastrMessage } from '@/utilities/Toastr.Utils';
import { getStockColorCodeCandidates } from '@/utilities/stockUtils';

interface IStockFetchPayload {
  stock_data: IStocksData['stock_data'];
  color_code?: string;
}

const fetchFirstAvailableStockPayload = async (
  colorCandidates: string[],
  fetchForColor: (colorCode: string) => Promise<IStockFetchPayload | null>
): Promise<{ stockPayload: IStockFetchPayload; resolvedColorCode: string } | null> =>
  colorCandidates.reduce<
    Promise<{ stockPayload: IStockFetchPayload; resolvedColorCode: string } | null>
  >(async (accPromise, colorCode) => {
    const acc = await accPromise;
    if (acc) {
      return acc;
    }

    const payload = await fetchForColor(colorCode);
    if (payload) {
      return { stockPayload: payload, resolvedColorCode: colorCode };
    }

    return null;
  }, Promise.resolve(null));

const mapCartItemToProduct = (
  item: ICartItemDetail,
  designGroupName: string
): IStoreOrderProduct => ({
  orderItemId: String(item.cartItemId ?? item.proofItemId),
  storeOrderItemId: item.id,
  order_item_id: item.alphaNumericProofItemId ?? String(item.cartItemId),
  alphaNumericOrderItemsId: item.alphaNumericProofItemId ?? String(item.cartItemId),
  apparelStyleCode: item.apparelStyleCode,
  apparelColorCode: item.apparelColorCode || '',
  apparelColorHex: item.apparelHex || '',
  apparelName: item.apparelName,
  imageUrl: item.primaryImage || item.designImgPrimary || '',
  designApparelSizes: item.designApparelSizes ?? item.apparelSizes ?? '',
  orderItemSizes: item.orderItemSizes ?? '',
  apparelSizes: item.designApparelSizes ?? item.apparelSizes ?? '',
  isExternalApparel: item.isExternalApparel ?? false,
  designGroup: designGroupName,
  proofItemId: item.proofItemId,
  cartItemId: item.cartItemId,
  designId: item.designId,
});

const buildOrderItemSizesMap = (
  existingGroup: ISizeFormGroup | undefined,
  designApparelSizes: string[],
  product: IStoreOrderProduct
): Record<string, boolean> => {
  if (existingGroup) {
    return Object.entries(existingGroup.sizesSelected).reduce<Record<string, boolean>>(
      (acc, [, value]) => {
        acc[value.label] = value.selected;
        return acc;
      },
      {}
    );
  }

  return buildOrderItemSizesFromDesignAndSelected(
    designApparelSizes,
    product.orderItemSizes,
    product.apparelSizes
  );
};

const buildInitialSizeGroup = (
  product: IStoreOrderProduct,
  existingGroup?: ISizeFormGroup,
  sizeOrderCounts: ISizeOrderCounts = {}
): ISizeFormGroup => {
  const baseGroupFields = {
    itemId: product.orderItemId,
    orderItemId: product.orderItemId,
    alphaNumericOrderItemsId: product.alphaNumericOrderItemsId,
    designGroupName: product.designGroup,
    stockValid: true,
    stockNotes: existingGroup?.stockNotes ?? { note: '', file: null },
    stocks: existingGroup?.stocks ?? null,
    stocksData: existingGroup?.stocksData ?? null,
  };

  if (existingGroup) {
    const soldOutRestrictions = applySoldOutRestrictionsToSizesSelected(
      existingGroup.sizesSelected,
      existingGroup.sizesAvailable,
      existingGroup.stocksData ?? null
    );

    return applySizeOrderLocksToSizeGroup(
      {
        ...existingGroup,
        ...baseGroupFields,
        sizesSelected: soldOutRestrictions.sizesSelected,
        sizesAvailable: soldOutRestrictions.sizesAvailable,
      },
      sizeOrderCounts
    );
  }

  const designApparelSizes = getSizesLabel(product.designApparelSizes || product.apparelSizes);
  const orderItemSizes = buildOrderItemSizesMap(existingGroup, designApparelSizes, product);

  const sizeConfig = buildSizeConfigurationLikeOrders(
    product.isExternalApparel,
    orderItemSizes,
    designApparelSizes
  );

  const sizesSelected = sizesSelectedToRecord(sizeConfig);

  const sizesAvailable = sizesAvailableToRecord(Object.values(sizesSelected));
  const soldOutRestrictions = applySoldOutRestrictionsToSizesSelected(
    sizesSelected,
    sizesAvailable,
    null
  );

  return applySizeOrderLocksToSizeGroup(
    {
      ...baseGroupFields,
      sizesAvailable: soldOutRestrictions.sizesAvailable,
      sizesSelected: soldOutRestrictions.sizesSelected,
    },
    sizeOrderCounts
  );
};

interface IUseStoreConfirmSizesParams {
  designGroups: DesignGroup[];
  sizesForm: UseFormReturn<ISizesFormValues>;
  isReadOnly?: boolean;
  isEditStorePage?: boolean;
  sizeOrderCounts?: ISizeOrderCounts;
  storeId?: string | number | null;
}

export const useStoreConfirmSizes = ({
  designGroups,
  sizesForm,
  isReadOnly = false,
  isEditStorePage = false,
  sizeOrderCounts = {},
  storeId,
}: IUseStoreConfirmSizesParams) => {
  const [productsWithStock, setProductsWithStock] = useState<Record<string, IStoreOrderProduct[]>>(
    {}
  );
  const [fileUploadLoading, setFileUploadLoading] = useState<Record<string, boolean>>({});
  const stockFetchTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  const stockFetchKeysRef = useRef<Record<string, string>>({});
  const [updateStore] = useUpdateStoreMutation();
  const [getS3PreSignedUrl] = useGetS3PreSignedUrlMutation();
  const [fetchStocks] = useLazyGetStocksQuery();

  const productsByGroup = useMemo(() => {
    const grouped: Record<string, IStoreOrderProduct[]> = {};
    designGroups.forEach((group) => {
      grouped[group.name] = group.items.map((item) => mapCartItemToProduct(item, group.name));
    });
    return grouped;
  }, [designGroups]);

  const allProducts = useMemo(() => Object.values(productsByGroup).flat(), [productsByGroup]);

  const syncFormFromDesignGroups = useCallback(() => {
    const currentSizes = sizesForm.getValues('sizes') ?? [];
    const existingByOrderItemId = new Map(currentSizes.map((group) => [group.orderItemId, group]));

    const neededIds = new Set(allProducts.map((p) => p.orderItemId));
    const nextGroups: ISizeFormGroup[] = allProducts.map((product) =>
      buildInitialSizeGroup(
        product,
        existingByOrderItemId.get(product.orderItemId),
        sizeOrderCounts
      )
    );

    const filtered = nextGroups.filter((g) => neededIds.has(g.orderItemId));
    sizesForm.setValue('sizes', filtered, { shouldValidate: true });
  }, [allProducts, sizeOrderCounts, sizesForm]);

  useEffect(() => {
    if (!designGroups.length) return;
    syncFormFromDesignGroups();
  }, [designGroups, syncFormFromDesignGroups]);

  const getSizeGroupIndex = useCallback(
    (orderItemId: string) =>
      (sizesForm.getValues('sizes') ?? []).findIndex((g) => g.orderItemId === orderItemId),
    [sizesForm]
  );

  const getSizeGroup = useCallback(
    (orderItemId: string): ISizeFormGroup | undefined => {
      const index = getSizeGroupIndex(orderItemId);
      if (index < 0) return undefined;
      return sizesForm.getValues(`sizes.${index}`);
    },
    [getSizeGroupIndex, sizesForm]
  );

  const updateSizeGroup = useCallback(
    (orderItemId: string, patch: Partial<ISizeFormGroup>) => {
      const index = getSizeGroupIndex(orderItemId);
      if (index < 0) return;
      const current = sizesForm.getValues(`sizes.${index}`);
      sizesForm.setValue(`sizes.${index}`, { ...current, ...patch }, { shouldValidate: true });
    },
    [getSizeGroupIndex, sizesForm]
  );

  const getSizesArrayForProduct = useCallback(
    (orderItemId: string): ISizeSelectedState[] => {
      const group = getSizeGroup(orderItemId);
      if (!group) return [];
      return Object.values(group.sizesSelected).map((size) =>
        mergeSoldOutSizeDisplayState(size, group.stocksData)
      );
    },
    [getSizeGroup]
  );

  const saveSizeChanges = useCallback(
    async (product: IStoreOrderProduct) => {
      if (!storeId || isReadOnly || isEditStorePage) return;
      const group = getSizeGroup(product.orderItemId);
      if (!group) return;

      const sizeCodeSource = product.designApparelSizes || product.apparelSizes;
      const sizesString = convertSizesToOrderItemFormat(group.sizesSelected, sizeCodeSource);
      const allowedSizesString = convertOrderItemSizesToAllowedSizes(sizesString);

      try {
        await updateStore(
          buildStoreOrderItemSizesUpdatePayload(Number(storeId), {
            id: product.storeOrderItemId,
            order_item_id: product.order_item_id,
            sizes: sizesString,
            allowed_sizes: allowedSizesString,
          })
        ).unwrap();
      } catch {
        /* size save is best-effort when store exists */
      }
    },
    [getSizeGroup, isEditStorePage, isReadOnly, storeId, updateStore]
  );

  const refreshProductsWithStock = useCallback(() => {
    const next: Record<string, IStoreOrderProduct[]> = {};
    Object.entries(productsByGroup).forEach(([groupName, products]) => {
      next[groupName] = products.map((product) => ({
        ...product,
        stocksData: getSizeGroup(product.orderItemId)?.stocksData ?? null,
      }));
    });
    setProductsWithStock(next);
  }, [getSizeGroup, productsByGroup]);

  const applyStockDataToProduct = useCallback(
    (product: IStoreOrderProduct, stocksData: IStocksData, stocks: object | null = null) => {
      const group = getSizeGroup(product.orderItemId);
      const noteValue = group?.stockNotes?.note ?? '';
      const isInvalid =
        Boolean(stocksData.stockProps) && !noteValue && !stocksData.isFpExclusiveAndLowOrOutOfStock;

      let soldOutRestrictions: ReturnType<typeof applySoldOutRestrictionsToSizesSelected> | null =
        null;

      if (group) {
        soldOutRestrictions = applySoldOutRestrictionsToSizesSelected(
          group.sizesSelected,
          group.sizesAvailable,
          stocksData
        );
      }

      const stockPatch: Partial<ISizeFormGroup> = {
        stocksData,
        stockValid: !isInvalid,
        ...(stocks ? { stocks } : {}),
      };

      if (soldOutRestrictions) {
        stockPatch.sizesSelected = soldOutRestrictions.sizesSelected;
        stockPatch.sizesAvailable = soldOutRestrictions.sizesAvailable;
      }

      updateSizeGroup(product.orderItemId, stockPatch);
      refreshProductsWithStock();
    },
    [getSizeGroup, refreshProductsWithStock, updateSizeGroup]
  );

  const applyNoStockInfoData = useCallback(
    (product: IStoreOrderProduct, resolvedColorCode: string) => {
      const group = getSizeGroup(product.orderItemId);
      const existingStocksData = group?.stocksData;

      if (existingStocksData) {
        applyStockDataToProduct(product, {
          ...existingStocksData,
          stockProps: createNoStockInfoMessages(product.order_item_id, true),
        });
        return;
      }

      applyStockDataToProduct(product, {
        color_code: resolvedColorCode,
        stock_data: [],
        stockDataOnSizes: [],
        selectedSizesStockInfo: [],
        stockProps: createNoStockInfoMessages(product.order_item_id, true),
        isFpExclusiveAndLowOrOutOfStock: false,
      });
    },
    [applyStockDataToProduct, getSizeGroup]
  );

  const fetchStockForProduct = useCallback(
    (product: IStoreOrderProduct) => {
      const group = getSizeGroup(product.orderItemId);
      const visibleSizeLabels = group
        ? Object.values(group.sizesSelected).map((size) => size.label)
        : getSizesLabel(product.designApparelSizes || product.apparelSizes);
      const fallbackColorCode = product.apparelColorCode || product.apparelColorHex;

      if (product.isExternalApparel) {
        applyStockDataToProduct(
          product,
          buildUnavailableStocksDataForProduct(
            visibleSizeLabels,
            fallbackColorCode,
            product.order_item_id
          )
        );
        return;
      }

      if (!product.apparelStyleCode?.trim()) {
        return;
      }

      const existingTimer = stockFetchTimers.current[product.orderItemId];
      if (existingTimer) clearTimeout(existingTimer);

      stockFetchTimers.current[product.orderItemId] = setTimeout(async () => {
        const colorCandidates = getStockColorCodeCandidates(
          product.apparelColorCode,
          product.apparelColorHex
        );

        const fetchResult = await fetchFirstAvailableStockPayload(
          colorCandidates,
          async (colorCode) => {
            try {
              const result = await fetchStocks({
                styleCode: product.apparelStyleCode,
                colorCode,
              }).unwrap();

              if (result?.data?.stock_data?.length) {
                return result.data;
              }
            } catch {
              /* try next color code candidate */
            }

            return null;
          }
        );

        const stockPayload = fetchResult?.stockPayload ?? null;
        const resolvedColorCode = fetchResult?.resolvedColorCode ?? fallbackColorCode;

        const currentGroup = getSizeGroup(product.orderItemId);
        const selectedLabels = currentGroup
          ? getSelectedSizeLabels(currentGroup.sizesSelected)
          : [];

        if (stockPayload) {
          const stocksData = buildStocksDataForProduct(
            stockPayload,
            selectedLabels,
            product.designApparelSizes || product.apparelSizes
          );

          applyStockDataToProduct(product, stocksData, [stockPayload]);
          return;
        }

        applyNoStockInfoData(product, resolvedColorCode);
      }, 300);
    },
    [applyNoStockInfoData, applyStockDataToProduct, fetchStocks, getSizeGroup]
  );

  const watchedSizes = sizesForm.watch('sizes');

  useEffect(() => {
    refreshProductsWithStock();
  }, [watchedSizes, refreshProductsWithStock]);

  const toggleSize = useCallback(
    (product: IStoreOrderProduct, sizeLabel: string) => {
      if (isReadOnly) return;
      const group = getSizeGroup(product.orderItemId);
      if (!group) return;

      const current = group.sizesSelected[sizeLabel];
      if (
        !current ||
        current.lockedByExistingOrder ||
        isSizeRestrictedByStock(sizeLabel, group.stocksData)
      ) {
        return;
      }

      const nextSelectedValue = !current.selected;
      if (
        isEditStorePage &&
        !canToggleSizeSelection(group.sizesSelected, sizeLabel, nextSelectedValue)
      ) {
        return;
      }

      const nextSelected = {
        ...group.sizesSelected,
        [sizeLabel]: { ...current, selected: nextSelectedValue, disabled: false },
      };

      updateSizeGroup(product.orderItemId, { sizesSelected: nextSelected });
      fetchStockForProduct(product);
      saveSizeChanges(product).catch(() => undefined);
      refreshProductsWithStock();
    },
    [
      fetchStockForProduct,
      getSizeGroup,
      isEditStorePage,
      isReadOnly,
      refreshProductsWithStock,
      saveSizeChanges,
      updateSizeGroup,
    ]
  );

  const removeSize = useCallback(
    (product: IStoreOrderProduct, sizeLabel: string) => {
      if (isReadOnly) return;
      const group = getSizeGroup(product.orderItemId);
      const sizeData = group?.sizesSelected[sizeLabel];
      if (!group || !sizeData?.addedByUser || sizeData.lockedByExistingOrder) return;

      const nextSelected = { ...group.sizesSelected };
      delete nextSelected[sizeLabel];

      const nextAvailable = {
        ...group.sizesAvailable,
        [sizeLabel]: false,
      };

      updateSizeGroup(product.orderItemId, {
        sizesSelected: nextSelected,
        sizesAvailable: nextAvailable,
      });
      fetchStockForProduct(product);
      saveSizeChanges(product).catch(() => undefined);
      refreshProductsWithStock();
    },
    [
      fetchStockForProduct,
      getSizeGroup,
      isReadOnly,
      refreshProductsWithStock,
      saveSizeChanges,
      updateSizeGroup,
    ]
  );

  const debouncedNoteChange = useMemo(
    () =>
      debounce((orderItemId: string, note: string) => {
        updateSizeGroup(orderItemId, {
          stockNotes: {
            ...getSizeGroup(orderItemId)?.stockNotes,
            note,
            file: getSizeGroup(orderItemId)?.stockNotes?.file ?? null,
          },
        });
        refreshProductsWithStock();
      }, 1000),
    [getSizeGroup, refreshProductsWithStock, updateSizeGroup]
  );

  const onNoteChange = useCallback(
    (orderItemId: string, note: string) => {
      if (isReadOnly) return;
      const group = getSizeGroup(orderItemId);
      if (!group) return;
      updateSizeGroup(orderItemId, {
        stockNotes: { ...group.stockNotes, note },
      });
      debouncedNoteChange(orderItemId, note);
    },
    [debouncedNoteChange, getSizeGroup, isReadOnly, updateSizeGroup]
  );

  const uploadNoteFile = useCallback(
    async (orderItemId: string, file: File) => {
      if (isReadOnly) return;
      if (file.size >= STORE_CONFIRM_SIZES_NOTE.MAX_FILE_SIZE_BYTES) {
        showToastrMessage('The file size is larger than 20MB.', 'failure');
        return;
      }

      setFileUploadLoading((prev) => ({ ...prev, [orderItemId]: true }));

      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('folder', FLAG_FILE_FOLDER);
        const signed = await getS3PreSignedUrl(formData).unwrap();

        await fetch(signed.signedRequest, {
          method: 'PUT',
          body: file,
          headers: {
            'Content-Type': file.type,
            'x-amz-acl': 'private',
          },
        });

        const group = getSizeGroup(orderItemId);
        if (group) {
          updateSizeGroup(orderItemId, {
            stockNotes: {
              ...group.stockNotes,
              file: signed.fileName,
            },
          });
        }
        refreshProductsWithStock();
      } catch {
        showToastrMessage('Error uploading note file. Please try again later.', 'failure');
      } finally {
        setFileUploadLoading((prev) => ({ ...prev, [orderItemId]: false }));
      }
    },
    [getSizeGroup, getS3PreSignedUrl, isReadOnly, refreshProductsWithStock, updateSizeGroup]
  );

  const removeNoteFile = useCallback(
    (orderItemId: string) => {
      if (isReadOnly) return;
      const group = getSizeGroup(orderItemId);
      if (!group) return;
      updateSizeGroup(orderItemId, {
        stockNotes: { ...group.stockNotes, file: null },
      });
      refreshProductsWithStock();
    },
    [getSizeGroup, isReadOnly, refreshProductsWithStock, updateSizeGroup]
  );

  useEffect(() => {
    allProducts.forEach((product) => {
      const stockFetchKey = [
        product.apparelStyleCode,
        product.apparelColorCode,
        product.apparelColorHex,
        product.orderItemId,
      ].join(':');

      if (stockFetchKeysRef.current[product.orderItemId] === stockFetchKey) {
        return;
      }

      stockFetchKeysRef.current[product.orderItemId] = stockFetchKey;
      fetchStockForProduct(product);
    });
  }, [allProducts, fetchStockForProduct]);

  const isFormValid = useMemo(() => {
    const groups = watchedSizes ?? [];
    if (!groups.length) return designGroups.length === 0;

    return groups.every((group) => {
      const hasSelectedSize = Object.values(group.sizesSelected).some((s) => s.selected);
      if (!hasSelectedSize) return false;
      if (group.stocksData?.stockProps && !group.stockNotes.note?.trim()) {
        return group.stockValid;
      }
      return true;
    });
  }, [designGroups.length, watchedSizes]);

  return {
    productsWithStock: Object.keys(productsWithStock).length ? productsWithStock : productsByGroup,
    getSizesArrayForProduct,
    toggleSize,
    removeSize,
    onNoteChange,
    uploadNoteFile,
    removeNoteFile,
    fileUploadLoading,
    isFormValid,
    getSizeGroup,
  };
};
